import { useState } from 'react';
import { aiAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Sparkles, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * ICD10Codes Component
 * Displays ICD-10 codes for a given diagnosis using AI lookup
 */
export function ICD10Codes({ diagnosis, autoLoad = false }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchCodes = async () => {
    if (!diagnosis || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await aiAPI.getICD10Codes(diagnosis, 5);
      setCodes(response.data.codes || []);
      setLoaded(true);
    } catch (err) {
      console.error('Failed to fetch ICD-10 codes:', err);
      setError('Failed to load codes');
    }
    setLoading(false);
  };

  const getConfidenceBadgeVariant = (confidence) => {
    switch (confidence) {
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  if (!diagnosis) return null;

  return (
    <div className="inline-flex flex-wrap items-center gap-1">
      {!loaded && !loading && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
                onClick={fetchCodes}
              >
                <Sparkles className="h-3 w-3 mr-1" />
                ICD-10
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Get ICD-10 codes for this diagnosis</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      {loading && (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading...
        </span>
      )}
      
      {error && (
        <span className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </span>
      )}
      
      {loaded && codes.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Show first code always */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  variant={getConfidenceBadgeVariant(codes[0].confidence)}
                  className="font-mono text-xs cursor-help"
                >
                  {codes[0].code}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-semibold">{codes[0].code}</p>
                <p className="text-sm">{codes[0].description}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Confidence: {codes[0].confidence}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Show additional codes if expanded */}
          {expanded && codes.slice(1).map((code, idx) => (
            <TooltipProvider key={idx}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    variant={getConfidenceBadgeVariant(code.confidence)}
                    className="font-mono text-xs cursor-help"
                  >
                    {code.code}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-semibold">{code.code}</p>
                  <p className="text-sm">{code.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Confidence: {code.confidence}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          
          {/* Toggle button for additional codes */}
          {codes.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
      )}
      
      {loaded && codes.length === 0 && (
        <span className="text-xs text-muted-foreground">No codes found</span>
      )}
    </div>
  );
}

/**
 * ICD10Display - Inline display for a single diagnosis with its codes
 */
export function ICD10Display({ diagnosis }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-sm">• {diagnosis}</span>
      <ICD10Codes diagnosis={diagnosis} />
    </div>
  );
}

export default ICD10Codes;
